/**
 * Native Sign in with Apple — bundled Capacitor builds only.
 *
 * The Apple twin of `nativeGoogleAuth.ts`, and deliberately the same shape: the
 * OS sheet returns an Apple `id_token`, which the app POSTs to
 * `/api/auth/apple-native` in exchange for the same opaque `session_id` the web
 * callback sets. No deep links, no PKCE, no bearer token.
 *
 * The web path (`authService.signInWithApple` → Supabase `/auth/v1/authorize` →
 * `AuthCallbackPage`) is untouched — callers branch on `isNative` before
 * reaching this module, exactly as they do for Google.
 *
 * Two things differ from Google and both are Apple-specific traps:
 *
 *  1. **The name arrives exactly once.** Apple puts it in neither the id_token
 *     nor any later response; `profile.givenName`/`familyName` are populated on
 *     a user's FIRST authorization of this app and are empty forever after,
 *     even after a reinstall. `getAppleIdToken` therefore returns them and the
 *     caller must forward them — dropping them loses the name permanently.
 *     "Revoke this app" in iOS Settings → Apple Account → Sign in with Apple is
 *     the only way to get a first authorization back for testing.
 *  2. **`aud` is the bundle id**, not a separate client ID. There is no Apple
 *     equivalent of `GOOGLE_CLIENT_IDS` to configure here: the native assertion
 *     is minted against `com.gotradeloop.mobile`, so that bundle id must appear
 *     in the Supabase Apple provider's "Client IDs" list alongside the web
 *     Services ID or every exchange 401s. See src/docs/APPLE_OAUTH_SETUP.md.
 */

import { SocialLogin } from '@capgo/capacitor-social-login';
import { APPLE_HIDDEN_EMAIL_NOTICE } from '@/config/auth-notices';

export type NativeAppleAuthErrorCode =
  /** The user dismissed the Apple sheet. Never surface this as an error. */
  | 'USER_CANCELLED'
  /** The sheet succeeded but returned no id_token — nothing to exchange. */
  | 'NO_ID_TOKEN'
  /** No WebCrypto in this WebView, so the nonce cannot be computed. */
  | 'NONCE_UNSUPPORTED'
  /** Anything else the plugin/SDK threw (missing entitlement, no network, …). */
  | 'PLUGIN_ERROR';

export class NativeAppleAuthError extends Error {
  constructor(
    message: string,
    public code: NativeAppleAuthErrorCode,
    public cause?: unknown
  ) {
    super(message);
    this.name = 'NativeAppleAuthError';
  }
}

/**
 * `initialize` is idempotent for our purposes but not free, so it runs once per
 * app session. A failure clears the memo so a later attempt can retry.
 *
 * `clientId`/`redirectUrl` are deliberately omitted: they exist for the plugin's
 * web/Android Apple flow, which we do not use — on iOS the native
 * ASAuthorization path derives everything from the app's own bundle id and
 * entitlement, and the plugin's own docs say to pass an empty redirect on iOS to
 * prevent a redirect. Android and web users get the Supabase redirect flow
 * instead (authService.signInWithApple).
 */
let initializePromise: Promise<void> | null = null;

export const initializeAppleSignIn = (): Promise<void> => {
  if (!initializePromise) {
    initializePromise = SocialLogin.initialize({
      apple: {},
    }).catch((error) => {
      initializePromise = null;
      throw error;
    });
  }
  return initializePromise;
};

/** Reset the one-shot initialize memo. Test seam only. */
export const resetAppleSignInInitialization = (): void => {
  initializePromise = null;
};

const toHex = (bytes: Uint8Array): string =>
  Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');

/**
 * The same nonce contract Google uses, and the same way to get it wrong:
 *
 *   hashed → Apple (the plugin passes it through verbatim; it does NOT hash for
 *            you, and Apple echoes it into the `nonce` claim)
 *   raw    → the backend (Supabase hashes it itself and compares)
 *
 * Reversed, the exchange fails with `401 INVALID_ID_TOKEN`, indistinguishable
 * from a bad audience.
 */
export const generateNonce = async (): Promise<{ raw: string; hashed: string }> => {
  const webCrypto = globalThis.crypto;
  if (!webCrypto?.subtle || !webCrypto.getRandomValues) {
    throw new NativeAppleAuthError(
      'This device cannot generate a secure sign-in nonce (WebCrypto unavailable).',
      'NONCE_UNSUPPORTED'
    );
  }

  const raw = toHex(webCrypto.getRandomValues(new Uint8Array(32)));
  const digest = await webCrypto.subtle.digest('SHA-256', new TextEncoder().encode(raw));
  return { raw, hashed: toHex(new Uint8Array(digest)) };
};

/**
 * Apple reports a dismissal as error 1001 (`ASAuthorizationErrorCanceled`), which
 * the plugin surfaces with varying shapes across versions — hence the code, the
 * numeric check and the message fallback. Mistaking a genuine failure for a
 * cancel costs a missing toast; mistaking a cancel for a failure shows the user
 * an error they did not cause.
 */
const isUserCancellation = (error: unknown): boolean => {
  const raw = error as { code?: string | number; message?: string } | null;
  const code = raw?.code;
  if (code === 'USER_CANCELLED' || code === 1001 || code === '1001') return true;
  const message = raw?.message?.toLowerCase() ?? '';
  return (
    message.includes('cancel') ||
    message.includes('access_denied') ||
    message.includes('1001')
  );
};

/**
 * Launch the OS Apple sign-in sheet and return the assertion to exchange.
 *
 * @returns the Apple `id_token`, the **raw** (unhashed) nonce to POST with it,
 *   and the first-authorization name if this is the first time this user has
 *   authorized the app (null on every later sign-in — see the module note).
 */
export const getAppleIdToken = async (): Promise<{
  idToken: string;
  nonce: string;
  givenName: string | null;
  familyName: string | null;
}> => {
  await initializeAppleSignIn();
  const { raw, hashed } = await generateNonce();

  let response: Awaited<ReturnType<typeof SocialLogin.login<'apple'>>>;
  try {
    response = await SocialLogin.login({
      provider: 'apple',
      options: {
        // 'name' is what makes Apple return givenName/familyName on the first
        // authorization. Without it the name is never sent at all, and because
        // there is no second chance the account is permanently nameless.
        scopes: ['name', 'email'],
        nonce: hashed,
      },
    });
  } catch (error) {
    if (isUserCancellation(error)) {
      throw new NativeAppleAuthError('Apple sign-in was cancelled.', 'USER_CANCELLED', error);
    }
    throw new NativeAppleAuthError(
      (error as { message?: string } | null)?.message || 'Apple sign-in failed.',
      'PLUGIN_ERROR',
      error
    );
  }

  const result = response.result;
  const idToken = result.idToken;
  if (!idToken) {
    throw new NativeAppleAuthError(
      'Apple did not return an identity token.',
      'NO_ID_TOKEN',
      response
    );
  }

  return {
    idToken,
    nonce: raw,
    givenName: result.profile?.givenName ?? null,
    familyName: result.profile?.familyName ?? null,
  };
};

/**
 * How the UI should react to a failed native Apple sign-in. Same contract as
 * `mapNativeGoogleError` — a pure function of the thrown error, so the page just
 * renders the outcome and the mapping is unit-testable without a device.
 *
 * `no_account` / `apple_hidden_email` are the account gate's
 * expected rejections (BETA_GATED_OAUTH_PLAN.md §2,
 * APPLE_HIDDEN_EMAIL_LINKING.md §6) — graceful copy, not a failure toast.
 *
 * `apple_hidden_email` gets its own kind rather than reusing `error` because
 * the two demand opposite treatment: `error` is rendered as a red failure toast
 * with a retry implication, and this is neither a failure nor retryable —
 * retrying with Hide My Email produces the identical result. It also must not
 * land the user on the signup tab, which is where `no_account` sends them.
 */
export type NativeAppleOutcome =
  | { kind: 'cancelled' }
  | { kind: 'no_account'; message: string }
  | { kind: 'apple_hidden_email'; message: string }
  | { kind: 'error'; title: string; message: string };

export const mapNativeAppleError = (error: unknown): NativeAppleOutcome => {
  if (error instanceof NativeAppleAuthError) {
    switch (error.code) {
      case 'USER_CANCELLED':
        return { kind: 'cancelled' };
      case 'NONCE_UNSUPPORTED':
        return {
          kind: 'error',
          title: 'Apple sign-in unavailable',
          message:
            'This device could not start a secure Apple sign-in. Please sign in with your email instead.',
        };
      default:
        return {
          kind: 'error',
          title: 'Apple sign-in failed',
          message: 'We could not complete Apple sign-in. Please try again.',
        };
    }
  }

  // Everything below is an ApiError from POST /api/auth/apple-native.
  const status = (error as { status?: number } | null)?.status;
  const code = (error as { data?: { code?: string } } | null)?.data?.code;

  if (code === 'NO_ACCOUNT') {
    return {
      kind: 'no_account',
      message: 'No account found for that Apple login — sign up first.',
    };
  }
  // The user has an account; Apple's relay alias just cannot be matched to it.
  // Must be checked explicitly — falling through to the generic branch below
  // would call this "Apple sign-in failed", and falling into `no_account` would
  // tell a user who HAS an account to sign up, which is the duplicate-account
  // bug (APPLE_HIDDEN_EMAIL_LINKING.md §3 #4).
  if (code === 'APPLE_HIDDEN_EMAIL') {
    return {
      kind: 'apple_hidden_email',
      message: APPLE_HIDDEN_EMAIL_NOTICE.message,
    };
  }
  if (status === 429) {
    return {
      kind: 'error',
      title: 'Too many attempts',
      message: 'Too many sign-in attempts. Please wait a few minutes and try again.',
    };
  }
  if (code === 'INVALID_ID_TOKEN' || code === 'MISSING_ID_TOKEN') {
    return {
      kind: 'error',
      title: 'Apple sign-in failed',
      message: 'Apple could not verify that sign-in. Please try again.',
    };
  }
  // Unrecognised failure. Carry the status/code in the copy — the generic
  // version of this message cost a full debugging round-trip on the Google path
  // because it looked identical whether the sheet, the exchange or the session
  // check had failed.
  return {
    kind: 'error',
    title: 'Apple sign-in failed',
    message: `Something went wrong signing you in (${status ?? 'network'}/${code ?? 'no-code'}). Please try again.`,
  };
};
